'use client';

import { useContext, type FC } from 'react';
import { Flex, Text, Pill } from '@/_components';
import { UserContext } from '@/_contexts/UserProvider';
import { setDefaultCompany } from '@/_api/client-companies';
import type { CompanySelectorProps } from './CompanySelector.types';
import { getClasses } from '@/_utils/classnames';
import styles from './CompanySelector.module.css';
import { User } from '@/_types/models/user';
import { useRouter } from 'next/navigation';
import { ToastContext } from '@/_contexts/ToastProvider';

export const CompanySelector: FC<CompanySelectorProps> = ({ onClose }) => {
  const { companies, user, token, setUser } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { push } = useRouter();

  return (
    <Flex isColumn={true} gap={2}>
      {companies.map((company, key) => {
        const isActive: boolean = user?.default_company_id === company.id;
        const companySelectorClasses: string = getClasses(
          styles.companySelector,
          {
            [styles.companySelectorActive]: isActive,
            [styles.companySelectorInactive]: company.inactive,
          },
        );

        const onClick = async () => {
          if (company?.inactive) return;

          try {
            await setDefaultCompany({ companyId: company.id, token });
            const newUser: User = {
              ...user!,
              default_company_id: company.id,
            };

            setUser(newUser);
            push('/');
          } catch (error) {
            addToast('error', null, { error });
          } finally {
            onClose?.();
          }
        };

        return (
          <Flex
            key={key}
            className={companySelectorClasses}
            onClick={onClick}
            xAlign="start"
            isColumn={true}
            gap={2}
          >
            <Flex isYCentered={true} gap={2}>
              <Text
                color={company?.inactive ? 'secondary' : 'primary'}
                fontWeight="semi-bold"
              >
                {company.name}
              </Text>
              {company?.inactive && (
                <Pill
                  label="Inactive"
                  intent="danger"
                  size="small"
                  isRound={true}
                />
              )}
            </Flex>
            <Text
              type="small"
              color={company?.inactive ? 'disabled' : 'secondary'}
            >
              {company.entity_type}
            </Text>
          </Flex>
        );
      })}
    </Flex>
  );
};
